import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { FiPlus, FiRotateCcw, FiDownload, FiUpload } from 'react-icons/fi';
import { Modal } from 'components/basicos';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import useKanbanPrefs from 'shared/hooks/useKanbanPrefs';
import { faseDeEstado } from './fases';
import PedidosActivos from './PedidosActivos';
import s from './styles';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const campo = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--agx-border)',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--agx-input-bg)',
  color: 'var(--agx-text)',
  fontFamily: 'inherit',
};

const checkboxStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--agx-text)' };

function ListaFiltro({ titulo, opciones, seleccionados, onToggle, getCodigo, getEtiqueta }) {
  const [busqueda, setBusqueda] = useState('');
  const filtradas = opciones.filter((o) => {
    const cod = getCodigo(o) || '';
    const etq = getEtiqueta(o) || '';
    return !busqueda || `${etq} ${cod}`.toLowerCase().includes(busqueda.toLowerCase());
  });
  return (
    <div style={{ border: '1px solid var(--agx-border)', borderRadius: '8px', padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>{titulo}</span>
        {seleccionados.length > 0 && (
          <span style={{ fontSize: '11px', color: 'var(--agx-pri2)' }}>{seleccionados.length} selec.</span>
        )}
      </div>
      <input
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar..."
        style={{ ...campo, padding: '6px 8px', fontSize: '12px', marginBottom: '6px' }}
      />
      <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {filtradas.length === 0 && <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>Sin resultados</span>}
        {filtradas.map((o) => {
          const cod = getCodigo(o);
          return (
            <label key={cod} style={{ ...checkboxStyle, cursor: 'pointer', fontSize: '12px' }}>
              <input type="checkbox" checked={seleccionados.includes(cod)} onChange={() => onToggle(cod)} />
              {getEtiqueta(o)}
              {o.total != null && <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>({o.total})</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

const PedidosPanel = ({
  pedidos,
  pedidoActivo,
  onSelectPedido,
  sedeFilter,
  onSedeFilterChange,
  sedes,
  puedeGestionar = false,
  puedeCrear = false,
}) => {
  const kanbanRef = useRef(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const { columnas, rename, move, reset, isCustom } = useKanbanPrefs();
  const { isTipoEmpresa } = useAuth();
  const esImportador = isTipoEmpresa('importador');

  // En importadoras, la ultima columna (llegada) se convierte en "Entregado" y
  // acumula el historial reciente de pedidos entregados (72h).
  const columnasVisibles = esImportador
    ? columnas.map((c) => (c.key === 'llegada' ? { ...c, label: 'Entregado' } : c))
    : columnas;

  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [creando, setCreando] = useState(false);
  const [form, setForm] = useState({ nombre: '', telefono: '', direccion: '', tipo_entrega: 'delivery', codigo_sede: '' });

  // Seleccion de cliente registrado (solo cuando la empresa usa registro completo)
  const [modoRegistroCompleto, setModoRegistroCompleto] = useState(false);
  const configCargadoRef = useRef(false);
  const [qCliente, setQCliente] = useState('');
  const [resultadosClientes, setResultadosClientes] = useState([]);
  const [buscandoClientes, setBuscandoClientes] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);

  const cargarConfigRegistro = useCallback(() => {
    if (configCargadoRef.current) return;
    configCargadoRef.current = true;
    API.get('/empresa-config/')
      .then((r) => {
        if (r.data.status === 'success' && r.data.data?.modo_registro === 'completo') {
          setModoRegistroCompleto(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!modoRegistroCompleto) return;
    if (clienteSeleccionado) {
      setResultadosClientes([]);
      setBuscandoClientes(false);
      return;
    }
    const q = (qCliente || '').trim();
    if (q.length < 2) {
      setResultadosClientes([]);
      setBuscandoClientes(false);
      return;
    }
    setBuscandoClientes(true);
    const t = setTimeout(async () => {
      try {
        const res = await API.get(`/clientes/buscar?q=${encodeURIComponent(q)}&limite=20`);
        if (res.data.status === 'success') setResultadosClientes(res.data.data || []);
      } catch (e) {
        setResultadosClientes([]);
      }
      setBuscandoClientes(false);
    }, 300);
    return () => clearTimeout(t);
  }, [qCliente, modoRegistroCompleto, clienteSeleccionado]);

  const seleccionarCliente = (c) => {
    setClienteSeleccionado(c);
    setForm((f) => ({
      ...f,
      nombre: [c.nombre, c.apellido].filter(Boolean).join(' ').trim(),
      telefono: c.telefono || '',
      direccion: c.direccion || '',
    }));
    setResultadosClientes([]);
  };

  const limpiarClienteSeleccionado = () => {
    setClienteSeleccionado(null);
    setQCliente('');
    setResultadosClientes([]);
  };

  // ── Archivo de pedido: descarga ──
  const [mostrarDescarga, setMostrarDescarga] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [descargaSede, setDescargaSede] = useState('');
  const [descOpciones, setDescOpciones] = useState({
    mostrar_modelos: true,
    mostrar_etiquetas: true,
    mostrar_stock: true,
    mostrar_unidad: true,
  });
  const [tagsDisponibles, setTagsDisponibles] = useState([]);
  const [tagsSeleccionadas, setTagsSeleccionadas] = useState([]);
  const [filtrosDisponibles, setFiltrosDisponibles] = useState({
    departamentos: [],
    marcas: [],
    grupos: [],
    categorias: [],
  });
  const [filtrosSeleccion, setFiltrosSeleccion] = useState({
    departamentos: [],
    marcas: [],
    grupos: [],
    subgrupos: [],
    categorias: [],
  });
  const [filtrarTags, setFiltrarTags] = useState(false);

  const subgruposOpciones = useMemo(() => {
    const out = [];
    (filtrosDisponibles.grupos || []).forEach((g) => {
      (g.subgrupos || []).forEach((sg) => {
        out.push({ codigo: sg.codigo, nombre: sg.nombre, grupo: g.nombre, total: sg.total });
      });
    });
    return out;
  }, [filtrosDisponibles.grupos]);

  const abrirDescarga = () => {
    setDescargaSede(sedeFilter || '');
    setDescOpciones({ mostrar_modelos: true, mostrar_etiquetas: true, mostrar_stock: true, mostrar_unidad: true });
    setFiltrosSeleccion({ departamentos: [], marcas: [], grupos: [], subgrupos: [], categorias: [] });
    setFiltrarTags(false);
    setMostrarDescarga(true);
    API.get('/pedido-archivo/filtros')
      .then((r) => {
        const data = r.data?.data || {};
        setTagsDisponibles(data.tags || []);
        setTagsSeleccionadas((data.tags || []).map((t) => t.id));
        setFiltrosDisponibles({
          departamentos: data.departamentos || [],
          marcas: data.marcas || [],
          grupos: data.grupos || [],
          categorias: data.categorias || [],
        });
      })
      .catch(() => {
        setTagsDisponibles([]);
        setTagsSeleccionadas([]);
        setFiltrosDisponibles({ departamentos: [], marcas: [], grupos: [], categorias: [] });
      });
  };

  const toggleTag = (id) => {
    setTagsSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleFiltro = (tipo, valor) => {
    setFiltrosSeleccion((prev) => {
      const actual = prev[tipo] || [];
      return {
        ...prev,
        [tipo]: actual.includes(valor) ? actual.filter((x) => x !== valor) : [...actual, valor],
      };
    });
  };

  const descargarArchivo = async () => {
    setDescargando(true);
    try {
      const params = new URLSearchParams();
      if (descargaSede) params.set('sede', descargaSede);
      params.set('mostrar_modelos', descOpciones.mostrar_modelos ? 'true' : 'false');
      params.set('mostrar_etiquetas', descOpciones.mostrar_etiquetas ? 'true' : 'false');
      params.set('mostrar_stock', descOpciones.mostrar_stock ? 'true' : 'false');
      params.set('mostrar_unidad', descOpciones.mostrar_unidad ? 'true' : 'false');
      if (descOpciones.mostrar_etiquetas && tagsSeleccionadas.length > 0) {
        params.set('tags', tagsSeleccionadas.join(','));
      }
      if (filtrarTags) params.set('filtrar_tags', 'true');
      const f = filtrosSeleccion;
      if (f.departamentos.length) params.set('departamento', f.departamentos.join(','));
      if (f.marcas.length) params.set('marca', f.marcas.join(','));
      if (f.grupos.length) params.set('grupo', f.grupos.join(','));
      if (f.subgrupos.length) params.set('subgrupo', f.subgrupos.join(','));
      if (f.categorias.length) params.set('categoria', f.categorias.join(','));
      const res = await API.get(`/pedido-archivo/exportar?${params.toString()}`, { responseType: 'arraybuffer' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers && res.headers['content-disposition'];
      let nombreArchivo = `Lista de Precios ${new Date().toISOString().slice(0, 10)}.xlsx`;
      if (cd) {
        const m = /filename="?([^"]+)"?/i.exec(cd);
        if (m && m[1]) nombreArchivo = m[1];
      }
      a.download = nombreArchivo;
      a.click();
      window.URL.revokeObjectURL(url);
      setMostrarDescarga(false);
    } catch (e) {
      alert('Error al descargar el archivo: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDescargando(false);
    }
  };

  // ── Archivo de pedido: carga ──
  const [mostrarCarga, setMostrarCarga] = useState(false);
  const [cargaArchivo, setCargaArchivo] = useState(null);
  const [cargaPreview, setCargaPreview] = useState(null);
  const [cargaError, setCargaError] = useState('');
  const [analizando, setAnalizando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [qClienteCarga, setQClienteCarga] = useState('');
  const [resultadosClienteCarga, setResultadosClienteCarga] = useState([]);
  const [clienteCarga, setClienteCarga] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);

  const abrirCarga = () => {
    setCargaArchivo(null);
    setCargaPreview(null);
    setCargaError('');
    setQClienteCarga('');
    setResultadosClienteCarga([]);
    setClienteCarga(null);
    setArrastrando(false);
    setMostrarCarga(true);
  };

  const aceptarArchivo = (file) => {
    if (!file) return;
    const nombre = (file.name || '').toLowerCase();
    if (!nombre.endsWith('.xlsx') && !nombre.endsWith('.xls')) {
      alert('Solo se aceptan archivos .xlsx o .xls');
      return;
    }
    setCargaArchivo(file);
    setCargaPreview(null);
    setCargaError('');
  };

  useEffect(() => {
    if (!mostrarCarga || clienteCarga) return;
    const q = (qClienteCarga || '').trim();
    if (q.length < 2) {
      setResultadosClienteCarga([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await API.get(`/clientes/buscar?q=${encodeURIComponent(q)}&limite=20`);
        if (res.data.status === 'success') setResultadosClienteCarga(res.data.data || []);
      } catch (e) {
        setResultadosClienteCarga([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [qClienteCarga, mostrarCarga, clienteCarga]);

  const analizarArchivo = async () => {
    if (!clienteCarga) {
      alert('Selecciona un cliente primero');
      return;
    }
    if (!cargaArchivo) {
      alert('Selecciona el archivo de pedido (.xlsx)');
      return;
    }
    setAnalizando(true);
    setCargaError('');
    setCargaPreview(null);
    try {
      const formData = new FormData();
      formData.append('archivo', cargaArchivo);
      formData.append('idcliente', clienteCarga.id);
      if (sedeFilter) formData.append('sede', sedeFilter);
      const res = await API.post('/pedido-archivo/analizar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (res.data.status === 'error') {
        setCargaError(res.data.message || 'No se pudo leer el archivo');
      } else {
        setCargaPreview(res.data.data || null);
      }
    } catch (e) {
      setCargaError(e?.response?.data?.detail || e?.response?.data?.message || 'Error al analizar el archivo');
    } finally {
      setAnalizando(false);
    }
  };

  const confirmarArchivo = async () => {
    if (!cargaPreview || !clienteCarga) return;
    const items = (cargaPreview.items || [])
      .filter((i) => !i.sin_stock)
      .map((i) => ({ codigo: i.codigo, cantidad: i.cantidad }));
    if (!items.length) {
      alert('No hay productos con stock para cargar');
      return;
    }
    setConfirmando(true);
    try {
      const res = await API.post('/pedido-archivo/ejecutar', {
        idcliente: clienteCarga.id,
        items,
        sede: sedeFilter || null,
        tipo_entrega: 'delivery',
      });
      if (res.data.status === 'success') {
        setMostrarCarga(false);
        onSelectPedido({ id: res.data.data.id, ...res.data.data });
      } else {
        alert(res.data.message || 'Error al crear el pedido');
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e?.response?.data?.message || 'Error al crear el pedido');
    } finally {
      setConfirmando(false);
    }
  };

  const abrirNuevo = () => {
    setForm({ nombre: '', telefono: '', direccion: '', tipo_entrega: 'delivery', codigo_sede: sedeFilter || '' });
    setClienteSeleccionado(null);
    setQCliente('');
    setResultadosClientes([]);
    setMostrarNuevo(true);
    cargarConfigRegistro();
  };

  const crearPedidoManual = async () => {
    if (!form.nombre.trim()) {
      alert('El nombre del cliente es obligatorio');
      return;
    }
    setCreando(true);
    try {
      const res = await API.post('/pedidos/crear-manual', {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim() || null,
        direccion: form.direccion.trim() || null,
        tipo_entrega: form.tipo_entrega,
        codigo_sede: form.codigo_sede || null,
        idcliente: clienteSeleccionado ? clienteSeleccionado.id : null,
      });
      if (res.data.status === 'success') {
        const id = res.data.data.id;
        setMostrarNuevo(false);
        onSelectPedido({ id, ...res.data.data });
      } else {
        alert(res.data.message || 'Error al crear el pedido');
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e?.response?.data?.message || 'Error al crear el pedido');
    }
    setCreando(false);
  };

  const CORTE_72H = Date.now() - 72 * 3600 * 1000;
  const activos = (pedidos || []).filter((p) => {
    if (p.estado === 'rechazado' || p.estado === 'cancelado') return false;
    if (p.estado === 'entregado' || p.estado === 'credito_pagado') {
      if (!esImportador) return false;
      const f = new Date(p.fechaactualizacion || p.fechapedido || 0).getTime();
      return f >= CORTE_72H;
    }
    return true;
  });

  const onMouseDown = useCallback((e) => {
    if (!kanbanRef.current) return;
    dragging.current = true;
    startX.current = e.pageX - kanbanRef.current.offsetLeft;
    scrollLeft.current = kanbanRef.current.scrollLeft;
    kanbanRef.current.style.cursor = 'grabbing';
    kanbanRef.current.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !kanbanRef.current) return;
    e.preventDefault();
    const x = e.pageX - kanbanRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    kanbanRef.current.scrollLeft = scrollLeft.current - walk;
  }, []);

  const onMouseUp = useCallback(() => {
    dragging.current = false;
    if (kanbanRef.current) {
      kanbanRef.current.style.cursor = 'grab';
      kanbanRef.current.style.removeProperty('user-select');
    }
  }, []);

  const onMouseLeave = useCallback(() => {
    if (dragging.current) {
      dragging.current = false;
      if (kanbanRef.current) {
        kanbanRef.current.style.cursor = 'grab';
        kanbanRef.current.style.removeProperty('user-select');
      }
    }
  }, []);

  const conFase = useMemo(() => {
    const c = {};
    columnasVisibles.forEach((f) => {
      c[f.key] = 0;
    });
    activos.forEach((p) => {
      const key = faseDeEstado(p.estado, esImportador);
      if (c[key] !== undefined) c[key]++;
    });
    return c;
  }, [activos, columnasVisibles, esImportador]);

  const fasesConActividad = columnasVisibles.filter((f) => conFase[f.key] > 0);

  return (
    <div style={s.pedidosContainer}>
      <div style={s.pedidosHeader}>
        <div style={s.pedidosHeaderLeft}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)' }}>
            Pedidos activos <span style={s.pedidosCount}>({activos.length})</span>
          </span>
        </div>
        <div style={s.pedidosHeaderRight}>
          {isCustom && (
            <button
              onClick={reset}
              title="Restablecer columnas por defecto"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid var(--agx-border)',
                background: 'var(--agx-surface)',
                color: 'var(--agx-muted)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <FiRotateCcw size={14} /> Restablecer
            </button>
          )}
          {puedeCrear && (
            <button
              onClick={abrirNuevo}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--agx-pri2)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <FiPlus size={14} /> Nuevo pedido
            </button>
          )}
          {puedeGestionar && (
            <>
              <button
                onClick={abrirDescarga}
                title="Descargar lista"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-text)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <FiDownload size={14} /> Descargar lista
              </button>
              <button
                onClick={abrirCarga}
                title="Cargar pedido desde archivo"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-text)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <FiUpload size={14} /> Cargar pedido
              </button>
            </>
          )}
          <select
            value={sedeFilter}
            onChange={(e) => onSedeFilterChange(e.target.value)}
            style={s.sedeSelect}
          >
            <option value="">Todas las sedes</option>
            {sedes.map((sed) => (
              <option key={sed.codigo} value={sed.codigo}>
                {sed.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Modal open={mostrarNuevo} onClose={() => setMostrarNuevo(false)} title="Nuevo pedido manual" width="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {modoRegistroCompleto && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>
                Cliente registrado
              </label>
              {clienteSeleccionado ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '8px 12px',
                    border: '1px solid var(--agx-border)',
                    borderRadius: '8px',
                    background: 'var(--agx-surface)',
                  }}
                >
                  <span style={{ fontSize: '13px', color: 'var(--agx-text)' }}>
                    {[clienteSeleccionado.nombre, clienteSeleccionado.apellido].filter(Boolean).join(' ')}
                    {clienteSeleccionado.telefono ? ` · ${clienteSeleccionado.telefono}` : ''}
                  </span>
                  <button
                    onClick={limpiarClienteSeleccionado}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--agx-muted)',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <>
                  <input
                    value={qCliente}
                    onChange={(e) => setQCliente(e.target.value)}
                    placeholder="Buscar por nombre, usuario, telefono o cedula"
                    style={campo}
                  />
                  {buscandoClientes && (
                    <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>Buscando...</span>
                  )}
                  {resultadosClientes.length > 0 && (
                    <div
                      style={{
                        maxHeight: '180px',
                        overflowY: 'auto',
                        border: '1px solid var(--agx-border)',
                        borderRadius: '8px',
                      }}
                    >
                      {resultadosClientes.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => seleccionarCliente(c)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid var(--agx-border)',
                            background: 'transparent',
                            color: 'var(--agx-text)',
                            fontSize: '13px',
                            cursor: 'pointer',
                          }}
                        >
                          {[c.nombre, c.apellido].filter(Boolean).join(' ')}
                          <span style={{ color: 'var(--agx-muted)', fontSize: '11px' }}>
                            {' '}
                            — {c.telefono || c.cedula || c.usuario || ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {!buscandoClientes &&
                    qCliente.trim().length >= 2 &&
                    resultadosClientes.length === 0 && (
                      <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>
                        Sin coincidencias
                      </span>
                    )}
                </>
              )}
              {clienteSeleccionado && (
                <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>
                  El pedido se asignara a este cliente y aparecera en su cuenta.
                </span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Nombre del cliente *</label>
            <input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre"
              style={campo}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Telefono</label>
            <input
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="Telefono"
              style={campo}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Direccion de entrega</label>
            <input
              value={form.direccion}
              onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              placeholder="Direccion"
              style={campo}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Tipo de entrega</label>
            <select
              value={form.tipo_entrega}
              onChange={(e) => setForm({ ...form, tipo_entrega: e.target.value })}
              style={campo}
            >
              <option value="delivery">Delivery</option>
              <option value="retiro">Retiro</option>
              <option value="encomienda">Encomienda</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Sede</label>
            <select
              value={form.codigo_sede}
              onChange={(e) => setForm({ ...form, codigo_sede: e.target.value })}
              style={campo}
            >
              <option value="">Sede por defecto</option>
              {sedes.map((sed) => (
                <option key={sed.codigo} value={sed.codigo}>
                  {sed.nombre}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={crearPedidoManual}
            disabled={creando}
            style={{
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: creando ? 0.6 : 1,
            }}
          >
            {creando ? 'Creando...' : 'Crear pedido'}
          </button>
        </div>
      </Modal>

      <Modal open={mostrarDescarga} onClose={() => setMostrarDescarga(false)} title="Descargar archivo de pedido" width="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Sede</label>
            <select value={descargaSede} onChange={(e) => setDescargaSede(e.target.value)} style={campo}>
              <option value="">Todas las sedes</option>
              {sedes.map((sed) => (
                <option key={sed.codigo} value={sed.codigo}>{sed.nombre}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Filtros (opcional)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <ListaFiltro
                titulo="Departamentos"
                opciones={filtrosDisponibles.departamentos}
                seleccionados={filtrosSeleccion.departamentos}
                onToggle={(v) => toggleFiltro('departamentos', v)}
                getCodigo={(o) => o.codigo}
                getEtiqueta={(o) => o.nombre}
              />
              <ListaFiltro
                titulo="Marcas"
                opciones={filtrosDisponibles.marcas}
                seleccionados={filtrosSeleccion.marcas}
                onToggle={(v) => toggleFiltro('marcas', v)}
                getCodigo={(o) => o.codigo}
                getEtiqueta={(o) => o.nombre}
              />
              <ListaFiltro
                titulo="Grupos"
                opciones={filtrosDisponibles.grupos}
                seleccionados={filtrosSeleccion.grupos}
                onToggle={(v) => toggleFiltro('grupos', v)}
                getCodigo={(o) => o.codigo}
                getEtiqueta={(o) => o.nombre}
              />
              <ListaFiltro
                titulo="Subgrupos"
                opciones={subgruposOpciones}
                seleccionados={filtrosSeleccion.subgrupos}
                onToggle={(v) => toggleFiltro('subgrupos', v)}
                getCodigo={(o) => o.codigo}
                getEtiqueta={(o) => (o.grupo ? `${o.nombre} (${o.grupo})` : o.nombre)}
              />
              <ListaFiltro
                titulo="Categorías (modelos)"
                opciones={filtrosDisponibles.categorias}
                seleccionados={filtrosSeleccion.categorias}
                onToggle={(v) => toggleFiltro('categorias', v)}
                getCodigo={(o) => o.codigo}
                getEtiqueta={(o) => o.nombre}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Opciones del archivo</label>
            <label style={checkboxStyle}>
              <input type="checkbox" checked={descOpciones.mostrar_modelos} onChange={(e) => setDescOpciones({ ...descOpciones, mostrar_modelos: e.target.checked })} />
              Mostrar modelos (secciones)
            </label>
            <label style={checkboxStyle}>
              <input type="checkbox" checked={descOpciones.mostrar_etiquetas} onChange={(e) => setDescOpciones({ ...descOpciones, mostrar_etiquetas: e.target.checked })} />
              Mostrar etiquetas (color + leyenda)
            </label>
            {descOpciones.mostrar_etiquetas && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '12px', maxHeight: '180px', overflowY: 'auto' }}>
                {tagsDisponibles.length === 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>No hay etiquetas configuradas</span>
                )}
                {tagsDisponibles.map((t) => (
                  <label key={t.id} style={{ ...checkboxStyle, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={tagsSeleccionadas.includes(t.id)}
                      onChange={() => toggleTag(t.id)}
                    />
                    <span
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '3px',
                        background: t.color || '#0B1A4A',
                        border: '1px solid var(--agx-border)',
                        flexShrink: 0,
                      }}
                    />
                    {t.nombre}
                    <span style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>({t.total_productos})</span>
                  </label>
                ))}
                {tagsDisponibles.length > 0 && (
                  <label style={{ ...checkboxStyle, cursor: 'pointer', marginTop: '4px', fontWeight: 600 }}>
                    <input type="checkbox" checked={filtrarTags} onChange={(e) => setFiltrarTags(e.target.checked)} />
                    Filtrar solo productos con estas etiquetas
                  </label>
                )}
              </div>
            )}
            <label style={checkboxStyle}>
              <input type="checkbox" checked={descOpciones.mostrar_stock} onChange={(e) => setDescOpciones({ ...descOpciones, mostrar_stock: e.target.checked })} />
              Mostrar stock
            </label>
            <label style={checkboxStyle}>
              <input type="checkbox" checked={descOpciones.mostrar_unidad} onChange={(e) => setDescOpciones({ ...descOpciones, mostrar_unidad: e.target.checked })} />
              Mostrar unidad
            </label>
          </div>
          <button
            onClick={descargarArchivo}
            disabled={descargando}
            style={{
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: descargando ? 0.6 : 1,
            }}
          >
            {descargando ? 'Generando...' : 'Descargar'}
          </button>
        </div>
      </Modal>

      <Modal open={mostrarCarga} onClose={() => setMostrarCarga(false)} title="Cargar pedido desde archivo" width="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Cliente *</label>
            {clienteCarga ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', border: '1px solid var(--agx-border)', borderRadius: '8px', background: 'var(--agx-surface)' }}>
                <span style={{ fontSize: '13px', color: 'var(--agx-text)' }}>
                  {[clienteCarga.nombre, clienteCarga.apellido].filter(Boolean).join(' ')}
                  {clienteCarga.telefono ? ` · ${clienteCarga.telefono}` : ''}
                </span>
                <button onClick={() => { setClienteCarga(null); setQClienteCarga(''); }} style={{ border: 'none', background: 'transparent', color: 'var(--agx-muted)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Cambiar</button>
              </div>
            ) : (
              <>
                <input value={qClienteCarga} onChange={(e) => setQClienteCarga(e.target.value)} placeholder="Buscar por nombre, usuario, telefono o cedula" style={campo} />
                {resultadosClienteCarga.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--agx-border)', borderRadius: '8px' }}>
                    {resultadosClienteCarga.map((c) => (
                      <button key={c.id} onClick={() => { setClienteCarga(c); setResultadosClienteCarga([]); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--agx-border)', background: 'transparent', color: 'var(--agx-text)', fontSize: '13px', cursor: 'pointer' }}>
                        {[c.nombre, c.apellido].filter(Boolean).join(' ')}
                        <span style={{ color: 'var(--agx-muted)', fontSize: '11px' }}> — {c.telefono || c.cedula || c.usuario || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-text)' }}>Archivo (.xlsx)</label>
            <div
              onClick={() => document.getElementById('carga-pedido-file').click()}
              onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArrastrando(false);
                const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                if (f) aceptarArchivo(f);
              }}
              onPaste={(e) => {
                const items = (e.clipboardData && e.clipboardData.items) || [];
                const archivoItem = Array.from(items).find((it) => it.kind === 'file');
                if (archivoItem) {
                  const f = archivoItem.getAsFile();
                  if (f) aceptarArchivo(f);
                }
              }}
              tabIndex={0}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '20px 12px',
                border: '2px dashed ' + (arrastrando ? 'var(--agx-pri2)' : 'var(--agx-border)'),
                borderRadius: '8px',
                background: arrastrando ? 'rgba(26,115,232,0.06)' : 'var(--agx-surface)',
                cursor: 'pointer',
                textAlign: 'center',
                color: 'var(--agx-muted)',
                outline: 'none',
              }}
            >
              <FiUpload size={22} />
              {cargaArchivo ? (
                <span style={{ fontSize: '13px', color: 'var(--agx-text)', fontWeight: 600 }}>{cargaArchivo.name}</span>
              ) : (
                <>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--agx-text)' }}>
                    Arrastra el archivo aquí, pégalo o haz clic
                  </span>
                  <span style={{ fontSize: '11px' }}>.xlsx / .xls</span>
                </>
              )}
            </div>
            <input
              id="carga-pedido-file"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => aceptarArchivo(e.target.files ? e.target.files[0] : null)}
            />
          </div>
          {!cargaPreview && (
            <button onClick={analizarArchivo} disabled={analizando} style={{ padding: '10px', borderRadius: '8px', border: 'none', background: 'var(--agx-pri2)', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: analizando ? 0.6 : 1 }}>
              {analizando ? 'Analizando...' : 'Analizar archivo'}
            </button>
          )}
          {cargaError && <span style={{ fontSize: '12px', color: '#C5221F' }}>{cargaError}</span>}
          {cargaPreview && (
            <>
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--agx-border)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--agx-surface)' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Producto</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Precio</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Stock</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cant.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cargaPreview.items || []).map((i, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid var(--agx-border)', background: i.sin_stock ? 'rgba(197,34,31,0.08)' : 'transparent' }}>
                        <td style={{ padding: '6px 8px' }}>
                          {i.nombre}
                          {i.sin_stock && <span style={{ display: 'block', color: '#C5221F', fontSize: '10px' }}>Sin stock (disponible: {i.stock})</span>}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{i.precio}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{i.stock}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{i.cantidad}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{i.subtotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(cargaPreview.no_encontrados || []).length > 0 && (
                <div style={{ fontSize: '12px', color: '#C5221F' }}>
                  No encontrados: {(cargaPreview.no_encontrados || []).map((n) => n.descripcion || n.codigo).join(', ')}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-text)' }}>Total</span>
                <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--agx-pri2)' }}>{cargaPreview.total}</span>
              </div>
              <button onClick={confirmarArchivo} disabled={confirmando} style={{ padding: '10px', borderRadius: '8px', border: 'none', background: '#188038', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: confirmando ? 0.6 : 1 }}>
                {confirmando ? 'Creando pedido...' : 'Confirmar pedido'}
              </button>
            </>
          )}
        </div>
      </Modal>

      {fasesConActividad.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '6px',
            padding: '6px 16px',
            flexWrap: 'wrap',
            backgroundColor: 'var(--agx-surface)',
            borderBottom: '1px solid var(--agx-border)',
            flexShrink: 0,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
          }}
          className="tp-dashboard"
        >
          {fasesConActividad.map((f) => (
            <span
              key={f.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '14px',
                fontSize: '11px',
                fontWeight: 600,
                background: isDark() ? f.color + '40' : f.color + '18',
                color: f.color,
                border: '1px solid ' + f.color + (isDark() ? '60' : '30'),
                whiteSpace: 'nowrap',
              }}
              className="tp-dash-badge"
            >
              {f.label} <span style={{ fontWeight: 700, fontSize: '12px' }}>{conFase[f.key]}</span>
            </span>
          ))}
        </div>
      )}

      <div
        ref={kanbanRef}
        style={s.kanbanContainer}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <PedidosActivos
          pedidos={activos}
          pedidoActivo={pedidoActivo}
          onSelectPedido={onSelectPedido}
          columnas={columnasVisibles}
          onRenameColumna={rename}
          onMoverColumna={move}
          esImportador={esImportador}
        />
      </div>
    </div>
  );
};

export default PedidosPanel;
